import React from 'react';
import { pxToRem } from '@withjoy/joykit/theme';
import { ArrowUp } from '@withjoy/joykit/icons';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { Box, BoxProps, ButtonV2, FabButton, FabButtonProps, Flex, TooltipV2 } from '@withjoy/joykit';
import { ButtonVariant } from '@apps/registry/common/components/Button';
import { ResponsiveBackgroundColorProps } from '@withjoy/joykit/styled-system';

export interface PageFabAreaProps
  extends Readonly<{
    preview?: FabButtonProps;
    scrollToTop?: {
      onClick: () => void;
    };
    addAction?: {
      label: string;
      icon?: React.ReactNode;
      mobileIcon?: React.ReactNode;
      variant?: ButtonVariant;
      onClick: () => void;
    } & ResponsiveBackgroundColorProps;
    hidePageSettingsAndFabCTA?: boolean;
    tooltip?: string;
    zIndex?: BoxProps['zIndex'];
  }> {}

const PageFabArea: React.FC<PageFabAreaProps> = ({ scrollToTop, preview, addAction, hidePageSettingsAndFabCTA = false, tooltip, zIndex }) => {
  const [isMobile] = useResponsive({ values: { mobile: true, tablet: false } }, false);
  return (
    <Flex
      display={hidePageSettingsAndFabCTA ? 'none' : 'inherit'}
      padding={6}
      height={pxToRem(104)}
      alignItems="center"
      justifyContent="center"
      position={['fixed', 'absolute']}
      bottom={0}
      left={0}
      width="100%"
      background="linear-gradient(180deg, rgba(255, 255, 255, 0) 0, #FFF 100%);"
      zIndex={zIndex}
    >
      <Flex
        width="100%"
        justifyContent="space-between"
        alignItems="center"
      >
        {/* Left small cta */}
        {scrollToTop && isMobile && addAction ? (
          <FabButton
            padding={pxToRem(14)}
            width={pxToRem(56)}
            height={pxToRem(56)}
            variant="ghostShadow"
            onClick={scrollToTop.onClick}
          >
            <ArrowUp size="sm" />
          </FabButton>
        ) : (
          <Flex
            width={pxToRem(56)}
            height={pxToRem(56)}
          />
        )}

        {/* Center large cta */}
        {isMobile && preview && (
          <FabButton
            width={pxToRem(168)}
            {...preview}
          />
        )}
        {isMobile && preview && addAction && (
          <TooltipV2
            placement="top"
            showArrow={true}
            overrides={{ Root: { props: !tooltip && { display: 'none' } }, Content: { props: { padding: 4 } } }}
            content={
              <Box
                typographyVariant="label2"
                color="mono4"
              >
                {tooltip}
              </Box>
            }
          >
            <FabButton
              padding={pxToRem(14)}
              width={pxToRem(56)}
              height={pxToRem(56)}
              variant="solid"
              backgroundColor="#000"
              onClick={addAction.onClick}
            >
              {addAction.mobileIcon}
            </FabButton>
          </TooltipV2>
        )}
        {((!isMobile && addAction) || (isMobile && !preview && addAction)) && (
          <TooltipV2
            placement="top"
            showArrow={true}
            overrides={{ Root: { props: !tooltip && { display: 'none' } }, Content: { props: { padding: 4 } } }}
            content={
              <Box
                typographyVariant="label2"
                color="mono4"
              >
                {tooltip}
              </Box>
            }
          >
            <ButtonV2
              height={pxToRem(56)}
              width={!preview && addAction ? pxToRem(257) : 'initial'}
              startIcon={addAction.icon}
              intent="neutral"
              variant={addAction.variant}
              backgroundColor={addAction.backgroundColor ?? '#000'}
              shape="rounded"
              onClick={addAction.onClick}
              {...(addAction.variant === 'outline' ? { borderColor: 'mono5' } : {})}
            >
              {addAction.label}
            </ButtonV2>
          </TooltipV2>
        )}
        {!preview && !addAction && (
          <Flex
            width={pxToRem(168)}
            height={pxToRem(56)}
          ></Flex>
        )}

        {/* Right small cta */}
        {scrollToTop && (!addAction || !isMobile) && (
          <FabButton
            padding={pxToRem(14)}
            width={pxToRem(56)}
            height={pxToRem(56)}
            variant="ghostShadow"
            onClick={scrollToTop.onClick}
          >
            <ArrowUp size="sm" />
          </FabButton>
        )}
        {!scrollToTop && (!addAction || !isMobile || (isMobile && !preview && addAction)) && (
          <Flex
            width={pxToRem(56)}
            height={pxToRem(56)}
          />
        )}
      </Flex>
    </Flex>
  );
};

export { PageFabArea };
